import { AUTH_CALLBACK_PATH, AUTH_LOGIN_PATH } from './constants';

/**
 * Reduce an untrusted `returnTo` (a URL query param, so attacker-controllable)
 * to a safe root-relative path, or `fallback` when it isn't one. This is the
 * open-redirect guard for the post-login navigation: `returnTo` rides through
 * `appState` into `router.navigate` / `<Navigate>`, both of which will follow a
 * cross-origin target. Only a path starting with a single `/` is allowed;
 * protocol-relative (`//host`), absolute (`https://host`), and backslash forms
 * the browser folds to `//` are rejected, with a URL-origin check as backstop.
 * The spent auth routes (`AUTH_CALLBACK_PATH` and `AUTH_LOGIN_PATH`, exact
 * pathname) are also rejected: returning to the callback re-enters the finished
 * exchange, and returning to login bounces an authed user back through the
 * login page instead of to application content.
 */
export function sanitizeReturnTo(value: string | null | undefined, fallback = '/'): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return fallback;
  }
  try {
    const url = new URL(value, 'https://x.invalid');
    const spent: string[] = [AUTH_CALLBACK_PATH, AUTH_LOGIN_PATH];
    // Compare without a trailing slash: the router matches `/login/` to the
    // `login` route, so the bare pathname alone would let it past the guard.
    // Guarded on length so the bare root stays '/' rather than collapsing to ''.
    const pathname = url.pathname.length > 1 ? url.pathname.replace(/\/+$/, '') : url.pathname;
    // React Router matches routes case-insensitively by default, so `/LOGIN`
    // must be rejected the same as `/login` — lowercase only for this
    // comparison, never the value returned below.
    if (
      url.origin !== 'https://x.invalid' ||
      spent.some((spentPath) => spentPath.toLowerCase() === pathname.toLowerCase())
    ) {
      return fallback;
    }
  } catch {
    return fallback;
  }
  return value;
}
